// src/screens/JoinScreen.tsx
//
// Pilot-readiness P3 (founder B1+B2, 2026-07-07) — the /join/<slug>
// registration screen that replaces student invite codes: the LINK carries
// the school; the student picks a username + password and gives the one-time
// written consent (recorded server-side as consent_records rows with the
// text version shown here). Staff invite codes are unaffected.

import { useEffect, useState } from 'react';
import { authApi, type RecoveryKit } from '../api/auth';
import { ApiError } from '../api/client';
import { useTokens } from '../state/theme';
import { ExamDateField } from '../components/ExamDateField';
import { PasswordField } from '../components/PasswordField';
import { RecoveryKitDisplay } from '../components/RecoveryKitDisplay';
import { useBrowserNavigationGuard } from '../hooks/useBrowserNavigationGuard';

/** Version id of the consent/informativa text below. Bump when the text
 *  changes — it is persisted on every consent_records row. */
export const CONSENT_TEXT_VERSION = 'privacy-aiact-v1-2026-07-07';

function toApiExamDate(value: string): string | undefined {
  const date = value.trim();
  return date ? `${date}T00:00:00.000Z` : undefined;
}

const ERROR_COPY: Record<string, string> = {
  school_not_found: 'Questo link di iscrizione non è valido. Chiedi alla tua autoscuola il link corretto.',
  invalid_username: 'Il nome utente può contenere solo lettere minuscole, numeri, punto, trattino e underscore (3–30 caratteri).',
  username_taken: 'Questo nome utente è già in uso — provane un altro.',
  weak_password: 'La password deve avere almeno 8 caratteri.',
  consent_required: "Per iscriverti devi accettare l'informativa.",
  validation_error: 'Controlla i campi e riprova.',
};

export function JoinScreen({ slug, onAuthed }: { slug: string; onAuthed: () => void }) {
  const tokens = useTokens();
  const [schoolName, setSchoolName] = useState<string | null>(null);
  const [badLink, setBadLink] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [examDate, setExamDate] = useState('');
  const [ageBracket, setAgeBracket] = useState<'under_18' | 'adult'>('adult');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryKit, setRecoveryKit] = useState<RecoveryKit | null>(null);
  const [recoveryKitUnavailable, setRecoveryKitUnavailable] = useState(false);
  useBrowserNavigationGuard(loading || recoveryKit !== null);

  useEffect(() => {
    let alive = true;
    authApi.joinInfo(slug)
      .then((info) => { if (alive) setSchoolName(info.schoolName); })
      .catch(() => { if (alive) setBadLink(true); });
    return () => { alive = false; };
  }, [slug]);

  const canSubmit =
    firstName.trim().length >= 1 &&
    username.trim().length >= 3 &&
    password.length >= 8 &&
    consent &&
    !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const result = await authApi.registerStudent({
        joinSlug: slug,
        firstName: firstName.trim(),
        username: username.trim().toLowerCase(),
        password,
        ageBracket,
        examDate: toApiExamDate(examDate),
        consentAccepted: true,
        consentTextVersion: CONSENT_TEXT_VERSION,
      });
      setPassword('');
      if (result.recoveryKit) setRecoveryKit(result.recoveryKit);
      else setRecoveryKitUnavailable(result.recoveryKitUnavailable);
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : undefined;
      setError(ERROR_COPY[code ?? ''] ?? 'Non riesco a completare l’iscrizione. Riprova tra poco.');
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    background: tokens.surface,
    border: `1px solid ${tokens.lineStrong}`,
    borderRadius: tokens.radius.md,
    padding: '10px 14px',
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  };
  const inputStyle: React.CSSProperties = {
    border: 'none', outline: 'none', background: 'transparent', width: '100%',
    color: tokens.text, fontFamily: tokens.fonts.body, fontSize: 15, minHeight: 24,
  };
  const eyebrowStyle: React.CSSProperties = {
    display: 'block', fontFamily: tokens.fonts.body, fontSize: 10.5, fontWeight: 800,
    letterSpacing: 0.9, textTransform: 'uppercase', color: tokens.accentInk, marginTop: 10,
  };

  if (badLink) {
    return (
      <div style={{ maxWidth: 460, margin: '0 auto', padding: 24, fontFamily: tokens.fonts.body, color: tokens.text }}>
        <h1 style={{ fontSize: 22 }}>Link non valido</h1>
        <p data-testid="join-bad-link">{ERROR_COPY.school_not_found}</p>
        <a href="/login" style={{ color: tokens.accentInk }}>Hai già un account? Accedi</a>
      </div>
    );
  }

  if (recoveryKit) {
    return (
      <div style={{ maxWidth: 460, margin: '0 auto', padding: '24px 16px', fontFamily: tokens.fonts.body, color: tokens.text }}>
        <RecoveryKitDisplay
          kit={recoveryKit}
          title="Salva il tuo kit di recupero"
          doneLabel="Ho salvato il kit"
          onDone={() => {
            setRecoveryKit(null);
            onAuthed();
          }}
        />
      </div>
    );
  }

  if (recoveryKitUnavailable) {
    return (
      <div style={{ maxWidth: 460, margin: '0 auto', padding: '24px 16px', fontFamily: tokens.fonts.body, color: tokens.text }}>
        <h1 style={{ fontSize: 22, marginBottom: 8 }}>Account creato, kit non disponibile</h1>
        <p role="status" style={{ color: tokens.text, fontSize: 14, lineHeight: 1.6, margin: '0 0 16px' }}>
          Il tuo account è stato creato e l’accesso è già attivo, ma il kit di recupero non è stato generato.
          Entra nell’app, apri <strong>Profilo → Kit di recupero</strong> e inserisci di nuovo la password per crearne uno.
        </p>
        <button type="button" onClick={onAuthed} style={{
          width: '100%', minHeight: 48, border: 'none', borderRadius: tokens.radius.md,
          background: tokens.accent, color: '#fff', fontFamily: tokens.fonts.body,
          fontSize: 15, fontWeight: 700, cursor: 'pointer',
        }}>
          Entra nell’app e crea il kit dal Profilo
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 460, margin: '0 auto', padding: '24px 16px', fontFamily: tokens.fonts.body, color: tokens.text }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>Iscriviti con Il Prof</h1>
      <p data-testid="join-school" style={{ color: tokens.textFaint, marginTop: 0 }}>
        {schoolName ? `Ti stai iscrivendo con ${schoolName}.` : 'Verifico il link della tua autoscuola…'}
      </p>

      <form onSubmit={handleSubmit}>
        <span style={eyebrowStyle}>Il tuo nome</span>
        <div style={fieldStyle}>
          <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)}
            placeholder="Come ti chiami?" autoCapitalize="words" autoCorrect="off"
            autoComplete="given-name" disabled={loading} aria-label="Il tuo nome" />
        </div>

        <span style={eyebrowStyle}>Nome utente</span>
        <div style={fieldStyle}>
          <input style={inputStyle} value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="es. giulia.rossi" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            autoComplete="username" disabled={loading} aria-label="Nome utente" />
        </div>

        <span style={eyebrowStyle}>Password</span>
        <PasswordField value={password} onChange={setPassword}
          placeholder="Almeno 8 caratteri" autoComplete="new-password" disabled={loading} />

        <span style={eyebrowStyle}>Data esame (opzionale)</span>
        <ExamDateField value={examDate} onChange={setExamDate} disabled={loading} />

        <fieldset style={{ border: 'none', padding: 0, margin: '10px 0 0' }}>
          <legend style={{ ...eyebrowStyle, marginTop: 0, padding: 0, marginBottom: 6 }}>Quanti anni hai?</legend>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['under_18', 'adult'] as const).map((b) => {
              const selected = ageBracket === b;
              return (
                <button key={b} type="button" onClick={() => setAgeBracket(b)} disabled={loading}
                  aria-pressed={selected}
                  style={{
                    flex: 1, minHeight: 44, borderRadius: tokens.radius.md, fontFamily: tokens.fonts.body,
                    fontSize: 14, fontWeight: selected ? 600 : 500, cursor: 'pointer',
                    background: selected ? tokens.accentSoft : tokens.surface,
                    color: selected ? tokens.accentInk : tokens.text,
                    border: `1px solid ${selected ? tokens.accent : tokens.lineStrong}`,
                  }}>
                  {b === 'under_18' ? 'Meno di 18' : '18 o più'}
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* B2 — the one-time written consent. Mandatory; recorded server-side
            with CONSENT_TEXT_VERSION. Under-18 self-declaration is part of the
            text (pilot approach per DECISIONS-2026-07-07 B2). */}
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
            disabled={loading} aria-label="Accetto l'informativa" style={{ width: 20, height: 20, marginTop: 2 }} />
          <span style={{ fontSize: 12.5, lineHeight: 1.5, color: tokens.text }}>
            Ho letto e accetto l’<strong>informativa privacy</strong> e so che studierò con un
            <strong> tutor di intelligenza artificiale</strong>, non con una persona (AI Act, art. 13).
            Se ho meno di 18 anni, dichiaro di avere il consenso di un genitore o tutore.
            Le mie risposte servono solo a personalizzare lo studio.
          </span>
        </label>

        {error && (
          <p role="alert" style={{ color: tokens.wrongText, fontSize: 13, marginTop: 10 }}>{error}</p>
        )}

        <button type="submit" disabled={!canSubmit} data-testid="join-submit"
          style={{
            width: '100%', marginTop: 16, minHeight: 48, borderRadius: tokens.radius.md, border: 'none',
            background: canSubmit ? tokens.accent : tokens.surface,
            color: canSubmit ? '#fff' : tokens.textFaint,
            fontFamily: tokens.fonts.body, fontSize: 15, fontWeight: 700,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}>
          {loading ? 'Un attimo…' : 'Inizia con Il Prof'}
        </button>
      </form>

      <p style={{ marginTop: 14, fontSize: 13, color: tokens.textFaint }}>
        Hai già un account? <a href="/login" style={{ color: tokens.accentInk }}>Accedi</a>
      </p>
    </div>
  );
}
